import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAttendance, fetchAttendanceByDate, checkAttendanceStatus, checkBulkAttendanceStatus, checkBulkAttendanceStatusWithLeaveInfo } from "../../../redux/attendanceSlice";
import { fetchLmsEmployees } from "../../../redux/erpSlice";
import { fetchHolidaysForDate } from "../../../redux/holidaySlice";
import { useTranslation } from "react-i18next";
import { Calendar, Search, RefreshCw, Info } from "lucide-react";

const LiveAttendance = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { attendanceRecords, status, error, bulkStatusResults } = useSelector((state) => state.attendance);
  const { lmsEmployees, loading: employeesLoading } = useSelector((state) => state.lmsEmployees);
  const { holidaysForDate } = useSelector((state) => state.holidays);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [enhancedBulkStatus, setEnhancedBulkStatus] = useState({});

  useEffect(() => {
    dispatch(fetchAttendance());
    dispatch(fetchLmsEmployees({ pageNumber: 1, pageSize: 100 }));
  }, [dispatch]);

  // Fetch attendance records when date changes
  useEffect(() => {
    dispatch(fetchAttendanceByDate(selectedDate));
    dispatch(fetchHolidaysForDate(selectedDate));
  }, [selectedDate, dispatch]);

  // Filter employees based on search term
  useEffect(() => {
    if (lmsEmployees.length > 0) {
      const filtered = lmsEmployees.filter(employee => 
        employee.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEmployees(filtered);
    }
  }, [lmsEmployees, searchTerm]);

  // Check enhanced bulk attendance status for all employees
  useEffect(() => {
    const checkEnhancedBulkAttendanceStatusForEmployees = async () => {
      if (filteredEmployees.length > 0) {
        const employeeEmails = filteredEmployees.map(employee => employee.email);
        try {
          const enhancedBulkStatus = await dispatch(checkBulkAttendanceStatusWithLeaveInfo({ 
            emails: employeeEmails, 
            date: selectedDate 
          })).unwrap();
          setEnhancedBulkStatus(enhancedBulkStatus);
        } catch (error) {
          console.error('Error checking enhanced bulk attendance status:', error);
        }
      }
    };

    if (filteredEmployees.length > 0) {
      checkEnhancedBulkAttendanceStatusForEmployees();
    }
  }, [filteredEmployees, selectedDate, dispatch]);

  const getEmployeeDetails = (employeeEmail) => {
    return filteredEmployees.find((emp) => emp.email === employeeEmail) || {};
  };

  const getAttendanceStatusMessage = (employeeEmail) => {
    const status = bulkStatusResults[employeeEmail];
    if (!status) return null;
    
    if (!status.canMarkAttendance) {
      return {
        type: "warning",
        message: status.reason
      };
    }
    return null;
  };

  const getAttendanceForEmployee = (employeeEmail) => {
    return attendanceRecords.find(record => 
      record.teacherEmail === employeeEmail && 
      record.date === selectedDate
    );
  };

  const getAttendanceStatus = (employee) => {
    const attendance = getAttendanceForEmployee(employee.email);
    const enhancedStatus = enhancedBulkStatus[employee.email];
    
    // Check if it's a holiday
    if (holidaysForDate && holidaysForDate.length > 0) {
      return {
        status: "Holiday",
        checkIn: "N/A",
        checkOut: "N/A",
        breaks: [],
        totalHours: 0,
        leaveInfo: null,
        holidayInfo: holidaysForDate.map(h => ({ name: h.name, description: h.description }))
      };
    }
    
    if (attendance) {
      return {
        status: attendance.status,
        checkIn: attendance.checkIn,
        checkOut: attendance.checkOut,
        breaks: attendance.breaks ? JSON.parse(attendance.breaks) : [],
        totalHours: attendance.totalHours,
        leaveInfo: enhancedStatus?.leaveInfo || null
      };
    } else {
      // Check if employee is on leave using enhanced status
      if (enhancedStatus && enhancedStatus.status === "On Leave") {
        return {
          status: "On Leave",
          checkIn: "N/A",
          checkOut: "N/A",
          breaks: [],
          totalHours: 0,
          leaveInfo: enhancedStatus.leaveInfo
        };
      }
      return {
        status: "Absent",
        checkIn: "N/A",
        checkOut: "N/A",
        breaks: [],
        totalHours: 0,
        leaveInfo: null
      };
    }
  };

  const getLeaveReasonDisplay = (leaveInfo) => {
    if (!leaveInfo) return null;
    
    return (
      <div className="mt-1 text-xs text-blue-600 bg-blue-50 p-1 rounded">
        <div className="flex items-center">
          <Info className="w-3 h-3 mr-1" />
          <span className="font-medium">{leaveInfo.leaveType}</span>
        </div>
        {leaveInfo.reason && (
          <div className="mt-1 text-gray-600">
            Reason: {leaveInfo.reason}
          </div>
        )}
      </div>
    );
  };

  const getHolidayDisplay = (holidayInfo) => {
    if (!holidayInfo || holidayInfo.length === 0) return null;
    
    return (
      <div className="mt-1 text-xs text-purple-600 bg-purple-50 p-1 rounded">
        <div className="flex items-center">
          <span className="text-lg mr-1">🎉</span>
          <span className="font-medium">{holidayInfo.map(h => h.name).join(', ')}</span>
        </div>
        {holidayInfo[0]?.description && (
          <div className="mt-1 text-gray-600">
            {holidayInfo[0].description}
          </div>
        )}
      </div>
    );
  };

  const summary = {
    present: 0,
    late: 0,
    onLeave: 0,
    absent: 0,
  };

  filteredEmployees.forEach((employee) => {
    const attendance = getAttendanceStatus(employee);
    
    switch (attendance.status?.toLowerCase()) {
      case "present":
        summary.present++;
        break;
      case "late":
        summary.late++;
        break;
      case "on leave":
        summary.onLeave++;
        break;
      case "holiday":
        // Don't count holidays in attendance summary
        break;
      case "absent":
        summary.absent++;
        break;
      default:
        break;
    }
  });

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = () => {
    dispatch(fetchAttendance());
    dispatch(fetchLmsEmployees({ pageNumber: 1, pageSize: 100 }));
  };

  const isToday = selectedDate === new Date().toISOString().split("T")[0];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-white rounded-lg shadow-md p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center mb-4 space-y-2 md:space-y-0">
              <div className="flex items-center space-x-4">
                <h2 className="text-lg md:text-xl font-semibold text-gray-800">
                  {isToday ? "Today's" : "Attendance"} Attendance 
                  <span className="text-gray-500">({selectedDate})</span>
                </h2>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleRefresh}
                  className="flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Refresh</span>
                </button>
              </div>
            </div>

            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by employee name or email"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-200">
                <thead>
                  <tr className="bg-gray-100 text-left text-sm text-gray-700">
                    <th className="py-2 px-3">EMPLOYEE EMAIL</th>
                    <th className="px-3">EMPLOYEE NAME</th>
                    <th className="px-3">LOCATION</th>
                    <th className="px-3">CHECK IN</th>
                    <th className="px-3">CHECK OUT</th>
                    <th className="px-3">BREAKS</th>
                    <th className="px-3">TOTAL HOURS</th>
                    <th className="px-3">STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {(status === "loading" || employeesLoading) && (
                    <tr>
                      <td colSpan="8" className="text-center py-4">Loading...</td>
                    </tr>
                  )}
                  {status === "failed" && (
                    <tr>
                      <td colSpan="8" className="text-center text-red-500">{error}</td>
                    </tr>
                  )}
                  {status === "succeeded" && filteredEmployees.length === 0 && (
                    <tr>
                      <td className="px-4 py-2 text-center text-gray-500" colSpan="8">
                        {searchTerm ? "No employees found matching your search." : "No employees available."}
                      </td>
                    </tr>
                  )}
                  {status === "succeeded" &&
                    filteredEmployees.map((employee) => {
                      const attendance = getAttendanceStatus(employee);
                      const breakText = attendance.breaks.map((b, i) => `Break ${i + 1}: ${b.breakIn || "N/A"} - ${b.breakOut || "N/A"}`).join(", ");
                      const statusMessage = getAttendanceStatusMessage(employee.email);
                      
                      return (
                        <tr key={employee.email} className="hover:bg-gray-200">
                          <td className="py-4 px-3">{employee.email}</td>
                          <td className="px-3">{employee.name || "N/A"}</td>
                          <td className="px-3">{employee.location || "N/A"}</td>
                          <td className="px-3">{attendance.checkIn || "N/A"}</td>
                          <td className="px-3">{attendance.checkOut || "N/A"}</td>
                          <td className="px-3">{breakText || "No breaks"}</td>
                          <td className="px-3">{attendance.totalHours || "N/A"}</td>
                          <td className="px-3">
                            <span className={`px-2 py-1 rounded text-xs ${
                              attendance.status === "Present" ? "bg-green-100 text-green-800" :
                              attendance.status === "Late" ? "bg-yellow-100 text-yellow-800" :
                              attendance.status === "On Leave" ? "bg-blue-100 text-blue-800" :
                              attendance.status === "Holiday" ? "bg-purple-100 text-purple-800" :
                              "bg-red-100 text-red-800"
                            }`}>
                              {attendance.status}
                            </span>
                            {attendance.holidayInfo && getHolidayDisplay(attendance.holidayInfo)}
                            {attendance.leaveInfo && getLeaveReasonDisplay(attendance.leaveInfo)}
                            {statusMessage && (
                              <div className="mt-1 text-xs text-orange-600">
                                {statusMessage.message}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">
              {isToday ? "Today's" : "Attendance"} Summary
            </h2>
            <ul className="space-y-2">
              {[
                { label: "Present", count: summary.present, color: "bg-green-100 text-green-800" },
                { label: "Late", count: summary.late, color: "bg-yellow-100 text-yellow-800" },
                { label: "On Leave", count: summary.onLeave, color: "bg-blue-100 text-blue-800" },
                { label: "Absent", count: summary.absent, color: "bg-red-100 text-red-800" },
              ].map((item) => (
                <li
                  key={item.label}
                  className="flex justify-between items-center text-sm text-gray-700 border-b border-gray-200 pb-2"
                >
                  <span>{item.label}</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${item.color}`}>
                    {item.count}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                <p><strong>Total Employees:</strong> {filteredEmployees.length}</p>
                <p><strong>Date:</strong> {new Date(selectedDate).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveAttendance;